import { useState, useMemo } from 'react';
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper,
    IconButton,
    Stack,
    Switch,
    FormControlLabel,
    Button,
    Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { IAdvisorActivity, IProject, AdvisorActivityInput } from '../../../types/database';
import { AdvisorActivityForm } from './AdvisorActivityForm';
import { useAdvisorShares } from '../hooks/useAdvisorShares';

interface AdvisorActivityTableProps {
    projectId?: number;
}

export const AdvisorActivityTable = ({ projectId }: AdvisorActivityTableProps) => {
    const [editMode, setEditMode] = useState(false);
    const [selectedActivity, setSelectedActivity] = useState<AdvisorActivityInput | undefined>();
    const [overrideMode, setOverrideMode] = useState(false);
    
    const { 
        activities, 
        fetched, 
        error,
        createActivity,
        updateActivity,
        reloadActivities 
    } = useAdvisorShares(projectId);

    const handleEdit = (activity: IAdvisorActivity) => {
        setSelectedActivity(activity);
        setEditMode(true);
        setOverrideMode(false);
    };

    const handleCreate = () => {
        setSelectedActivity(undefined);
        setEditMode(true);
        setOverrideMode(false);
    };

    const handleCreateOverride = (activity: IAdvisorActivity) => {
        // Create a copy of the activity but with project set
        const overrideActivity: AdvisorActivityInput = {
            key: activity.key,
            name: `${activity.name} (Override)`,
            description: activity.description,
            default_share_value: activity.default_share_value,
            is_active: activity.is_active,
            is_system_wide: false,
            project: projectId ? { id: projectId } as IProject : undefined,
            system_activity: activity
        };
        setSelectedActivity(overrideActivity);
        setEditMode(true);
        setOverrideMode(true);
    };

    const handleToggleActive = async (activity: IAdvisorActivity) => {
        // Only allow toggling active status for project-specific activities
        if (!projectId || activity.project?.id === projectId) {
            try {
                await updateActivity(activity.id, { is_active: !activity.is_active });
                await reloadActivities();
            } catch (error) {
                console.error('Error toggling activity status:', error);
            }
        }
    };

    const filteredActivities = useMemo(() => {
        if (!activities) return [];
        
        // Get all project-specific activities
        const projectSpecificActivities = activities.filter(activity => 
            activity.project?.id === projectId && activity.is_active
        );
        
        // Get system-wide activities that don't have a project override
        const systemWideActivities = activities.filter(activity => 
            !activity.project && // is system-wide
            activity.is_active &&
            !projectSpecificActivities.some(override => override.key === activity.key) // no override exists
        );

        const inactiveActivities = activities.filter(activity => !activity.is_active).sort((a, b) => a.name.localeCompare(b.name));

        const activeActivities = [...projectSpecificActivities, ...systemWideActivities].sort((a, b) => a.name.localeCompare(b.name));
        
        // Combine both arrays
        return [...activeActivities, ...inactiveActivities];
    }, [activities, projectId]);

    if (!fetched) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error loading activities: {error.message}</div>;
    }

    return (
        <div className="admin-table-container">
            <div className="admin-table-header">
                <h3>Advisor Activities</h3>
                <button 
                    className="btn btn-primary" 
                    onClick={handleCreate}
                >
                    Create Activity
                </button>
            </div>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Key</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Share Value</TableCell>
                            <TableCell>Scope</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredActivities.map((activity) => {
                            const isSystemWide = !activity.project;
                            const isProjectSpecific = !activity.is_system_wide;
                            const canEdit = !projectId || isProjectSpecific;
                            const canToggle = !projectId || isProjectSpecific;

                            return (
                                <TableRow key={activity.id} sx={{ backgroundColor: activity.is_active ? 'white' : '#f5f5f5' }}>
                                    <TableCell>{activity.key}</TableCell>
                                    <TableCell>{activity.name}</TableCell>
                                    <TableCell>{activity.description}</TableCell>
                                    <TableCell>{activity.default_share_value}</TableCell>
                                    <TableCell>
                                        {isProjectSpecific ? (
                                            <span style={{ color: '#1976d2' }}>Project Specific</span>
                                        ) : (
                                            <span style={{ color: '#666' }}>System Wide</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={activity.is_active}
                                                    onChange={() => handleToggleActive(activity)}
                                                    disabled={!canToggle}
                                                    color="primary"
                                                />
                                            }
                                            label={activity.is_active ? "Active" : "Inactive"}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            {canEdit ? (
                                                <Tooltip title="Edit Activity">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => handleEdit(activity)}
                                                        color="primary"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Create Project Override">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => handleCreateOverride(activity)}
                                                        color="primary"
                                                    >
                                                        <ContentCopyIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <AdvisorActivityForm
                activity={selectedActivity as IAdvisorActivity | undefined}
                reloadActivities={reloadActivities}
                editMode={editMode}
                setEditMode={setEditMode}
                projectId={projectId}
                isOverride={overrideMode}
                createActivity={createActivity}
                updateActivity={updateActivity}
            />
        </div>
    );
}; 